/**
 * @module modules/triage-panel-sk/buckets-controller
 * @description Reactive controller encapsulating triage bucket state management and localStorage persistence.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: all[name]
    });
}
_export(exports, {
    updateBucketDirtyState: function() {
        return updateBucketDirtyState;
    },
    TriageBucketsController: function() {
        return TriageBucketsController;
    }
});
const _errorMessage = require("../../../elements-sk/modules/errorMessage");
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const STORAGE_KEY = 'perf-user-triage-buckets-v3';
function updateBucketDirtyState(bucket) {
    if (bucket.triageState === 'NEW') {
        return bucket;
    }
    const lastApplied = new Set(bucket.lastAppliedIds || []);
    const hasUnapplied = bucket.anomalies.some((a)=>!lastApplied.has(a.id));
    return _object_spread_props(_object_spread({}, bucket), {
        triageState: hasUnapplied ? 'DIRTY' : 'APPLIED'
    });
}
let TriageBucketsController = class TriageBucketsController {
    hostConnected() {
        this.loadBuckets();
    }
    loadBuckets() {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const loaded = JSON.parse(saved);
                this.buckets = loaded.map((b)=>_object_spread_props(_object_spread({}, b), {
                        triageState: b.triageState || 'NEW',
                        anomalies: b.anomalies || []
                    }));
                this.host.requestUpdate();
            } else {
                this.buckets = [];
                this.host.requestUpdate();
            }
        } catch (e) {
            console.error('Failed to load triage buckets from localStorage', e);
        }
    }
    saveBuckets() {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this.buckets));
        } catch (e) {
            console.error('Failed to save triage buckets to localStorage', e);
        }
        this.host.requestUpdate();
        this.emitBucketsChanged();
    }
    emitBucketsChanged() {
        this.host.dispatchEvent(new CustomEvent('buckets-changed', {
            detail: {
                buckets: this.buckets
            },
            bubbles: true,
            composed: true
        }));
    }
    addNewBucket(name) {
        const trimmed = name.trim();
        if (!trimmed) return false;
        if (this.buckets.some((b)=>b.name === trimmed)) {
            (0, _errorMessage.errorMessage)(`Bucket "${trimmed}" already exists.`);
            return false;
        }
        this.buckets = [
            ...this.buckets,
            {
                name: trimmed,
                anomalies: [],
                triageState: 'NEW'
            }
        ];
        this.saveBuckets();
        return true;
    }
    updateBucket(updated) {
        this.buckets = this.buckets.map((b)=>b.name === updated.name ? updated : b);
        this.saveBuckets();
    }
    deleteBucket(name) {
        this.buckets = this.buckets.filter((b)=>b.name !== name);
        this.saveBuckets();
    }
    addToBucket(name, anomaliesToAdd) {
        this.buckets = this.buckets.map((b)=>{
            if (b.name === name) {
                const existingIds = new Set(b.anomalies.map((a)=>a.id));
                const newAnomalies = [
                    ...b.anomalies
                ];
                anomaliesToAdd.forEach((a)=>{
                    if (!existingIds.has(a.id)) {
                        newAnomalies.push(a);
                    }
                });
                const updated = _object_spread_props(_object_spread({}, b), {
                    anomalies: newAnomalies
                });
                return updateBucketDirtyState(updated);
            }
            return b;
        });
        this.saveBuckets();
    }
    getStagedAnomalyIds() {
        const ids = new Set();
        this.buckets.forEach((b)=>b.anomalies.forEach((a)=>ids.add(a.id)));
        return ids;
    }
    copyAll() {
        const lines = [];
        for (const b of this.buckets){
            lines.push(`[${b.name}]`);
            for (const a of b.anomalies){
                lines.push(`[${a.start_revision}-${a.end_revision}]:${a.test_path}:${a.id}`);
            }
            lines.push('');
        }
        const text = lines.join('\n').trim();
        navigator.clipboard.writeText(text);
        return text;
    }
    async pasteAll(anomalies) {
        try {
            const text = await navigator.clipboard.readText();
            if (!text) return false;
            const lines = text.split(/\r?\n/);
            let currentBucketName = 'Imported Bucket';
            const parsedBuckets = new Map();
            for (const line of lines){
                const trimmed = line.trim();
                if (!trimmed) continue;
                if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                    currentBucketName = trimmed.slice(1, -1).trim();
                    if (!parsedBuckets.has(currentBucketName)) {
                        parsedBuckets.set(currentBucketName, []);
                    }
                } else {
                    var _parts_pop;
                    const parts = trimmed.split(':');
                    const id = (_parts_pop = parts.pop()) === null || _parts_pop === void 0 ? void 0 : _parts_pop.trim();
                    if (id) {
                        if (!parsedBuckets.has(currentBucketName)) {
                            parsedBuckets.set(currentBucketName, []);
                        }
                        parsedBuckets.get(currentBucketName).push(id);
                    }
                }
            }
            if (parsedBuckets.size === 0) return false;
            const newBuckets = [
                ...this.buckets
            ];
            parsedBuckets.forEach((pastedIds, bucketName)=>{
                const matching = anomalies.filter((a)=>pastedIds.includes(a.id));
                const existingIndex = newBuckets.findIndex((b)=>b.name === bucketName);
                if (existingIndex >= 0) {
                    const existingBucket = newBuckets[existingIndex];
                    const existingIds = new Set(existingBucket.anomalies.map((a)=>a.id));
                    const mergedAnomalies = [
                        ...existingBucket.anomalies
                    ];
                    matching.forEach((m)=>{
                        if (!existingIds.has(m.id)) {
                            mergedAnomalies.push(m);
                        }
                    });
                    newBuckets[existingIndex] = updateBucketDirtyState(_object_spread_props(_object_spread({}, existingBucket), {
                        anomalies: mergedAnomalies
                    }));
                } else {
                    newBuckets.push({
                        name: bucketName,
                        anomalies: matching,
                        triageState: 'NEW'
                    });
                }
            });
            this.buckets = newBuckets;
            this.saveBuckets();
            return true;
        } catch (_e) {
            (0, _errorMessage.errorMessage)('Failed to read from clipboard. Please check browser clipboard permissions.');
            return false;
        }
    }
    constructor(host){
        this.buckets = [];
        this.host = host;
        host.addController(this);
    }
};
